// src/pages/providers/2_dashboard/components/StaffFilterControls.js
//
// Search & filter UI for the Staff (provider accounts) page. Mirrors the patient
// dashboard's Search & Filter popover + deletable filter badges
// (see ClinicianDashboard.js), but scoped to the three fields staff support:
// name substring, email substring, and a "date added" range.
//
// State lives in the parent page (ProviderUserDashboard) so it can be
// session-persisted via useSessionState and consumed by ProviderAccountsTable —
// exactly how ClinicianDashboard owns the patient filters and passes them to
// PatientTable. These components are purely presentational.

import React, { useState, useEffect } from 'react';
import { SearchIcon } from '@heroicons/react/solid';
import * as Popover from '@radix-ui/react-popover';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";

// Default/empty staff filter state. Exported so the page can seed its
// session-persisted state and clear individual filters cleanly.
export const DEFAULT_STAFF_FILTERS = {
  staffName: '',
  emailAddress: '',
  dateRange: {
    startDate: null,
    endDate: null,
  },
};

// Popover with the staff filter inputs. Commits via "Apply Filters" so a single
// state update flows to the parent (matching the patient popover's behaviour).
export function StaffFilterPopover({ onApplyFilters, initialValues, isOpen, onOpenChange }) {
  const [localFormInputs, setLocalFormInputs] = useState({
    staffName: initialValues.staffName || '',
    emailAddress: initialValues.emailAddress || '',
    dateRange: {
      startDate: initialValues.dateRange?.startDate || null,
      endDate: initialValues.dateRange?.endDate || null,
    },
  });

  const handleLocalInputChange = (field, value) => {
    setLocalFormInputs((prev) => ({ ...prev, [field]: value }));
  };

  // Re-seed the form from the current filters each time the popover opens. A
  // filter cleared via its badge updates the parent only, so without this the
  // form would keep the stale typed value (e.g. a removed name still showing).
  // Keyed on `isOpen` only — re-seeding on initialValues identity changes could
  // wipe in-progress edits, and the parent filters only change on Apply/remove,
  // both of which close the popover first.
  useEffect(() => {
    if (!isOpen) return;
    setLocalFormInputs({
      staffName: initialValues.staffName || '',
      emailAddress: initialValues.emailAddress || '',
      dateRange: {
        startDate: initialValues.dateRange?.startDate || null,
        endDate: initialValues.dateRange?.endDate || null,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  return (
    <Popover.Root open={isOpen} onOpenChange={onOpenChange}>
      <Popover.Trigger asChild>
        <button
          className="flex flex-row items-center justify-center p-2 rounded-xl transition-colors duration-200 hover:bg-gray-100"
          aria-label="Open staff filter panel"
        >
          <SearchIcon className="w-5 h-5 mr-1 text-gray-600" />
          <span className="text-gray-600 text-xs font-medium">Search & Filter</span>
        </button>
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          className="text-xs bg-white border border-gray-300 rounded-lg shadow-lg p-4 w-[300px] animate-in fade-in zoom-in-95 duration-200"
          sideOffset={5}
        >
          <div className="flex flex-col gap-4">
            <div>
              <label className="block font-medium text-gray-700 mb-1">
                Staff name contains:
              </label>
              <input
                type="text"
                value={localFormInputs.staffName}
                onChange={(e) => handleLocalInputChange('staffName', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                placeholder="Enter staff name..."
              />
            </div>

            <div>
              <label className="block font-medium text-gray-700 mb-1">
                Email address contains:
              </label>
              <input
                type="text"
                value={localFormInputs.emailAddress}
                onChange={(e) => handleLocalInputChange('emailAddress', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                placeholder="Enter email..."
              />
            </div>

            <div>
              <label className="block font-medium text-gray-700 mb-1">
                Date added:
              </label>
              <div className="flex items-center gap-2">
                <DatePicker
                  selectsRange={true}
                  startDate={localFormInputs.dateRange.startDate}
                  endDate={localFormInputs.dateRange.endDate}
                  onChange={(update) => {
                    const [start, end] = update;
                    setLocalFormInputs((prev) => ({
                      ...prev,
                      dateRange: { startDate: start, endDate: end },
                    }));
                  }}
                  isClearable={true}
                  placeholderText="Select date range..."
                  className="w-[266px] px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                  dateFormat="dd MMM yyyy"
                />
              </div>
            </div>

            <button
              onClick={() => onApplyFilters(localFormInputs)}
              className="font-semibold w-full mt-2 px-4 py-2 bg-violet-700 text-white rounded-lg hover:bg-violet-600 transition-colors duration-200"
            >
              Apply Filters
            </button>
          </div>
          <Popover.Arrow className="fill-white" />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

// Deletable badges for each active staff filter. Clicking a badge clears just
// that filter (mirrors the patient badge UX).
export function StaffFilterBadges({ activeFilters, onRemoveFilter }) {
  const getActiveFilterBadges = () => {
    return Object.entries(activeFilters).filter(([field, value]) => {
      if (value == null) return false;
      if (typeof value === 'string') return value.trim() !== '';
      if (field === 'dateRange') return value.startDate && value.endDate;
      return false;
    });
  };

  return (
    <>
      {getActiveFilterBadges().map(([field, value]) => (
        <button
          onClick={() => onRemoveFilter(field)}
          key={field}
          className="text-xs inline-flex items-center px-2 py-0.5 rounded-md font-medium bg-blue-100 text-blue-900 hover:bg-red-200 hover:text-red-800 transition-colors duration-200"
        >
          <p className="mr-1">×</p>
          {field === 'staffName' && `Name contains '${value}'`}
          {field === 'emailAddress' && `Email contains '${value}'`}
          {field === 'dateRange' && value.startDate && value.endDate &&
            `Added ${value.startDate.toLocaleDateString()} - ${value.endDate.toLocaleDateString()}`}
        </button>
      ))}
    </>
  );
}
